import {
    Avatar,
    Badge,
    Box,
    HStack,
    Spinner,
    Table,
    TableContainer,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr,
    VStack,
    useColorModeValue
} from '@chakra-ui/react';
import { translate } from '../../../i18n';
import { OrderDto, OrderStatus } from '../../../models/order/dto/order.dto';
import ErrorState from '../../../components/states/ErrorState';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const formatOrderDate = (dateString: string) => {
    const d = new Date(dateString);
    const month = MONTHS[d.getMonth()];
    const day = d.getDate().toString().padStart(2, '0');
    const year = d.getFullYear();
    const hours = d.getHours().toString().padStart(2, '0');
    const minutes = d.getMinutes().toString().padStart(2, '0');
    return `${month} ${day}, ${year} ${hours}:${minutes}`;
};

interface GameOrdersCardProps {
    orders: OrderDto[] | undefined;
    isLoading: boolean;
    error: unknown;
}

export default function GameOrdersCard({ orders, isLoading, error }: GameOrdersCardProps) {
    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <Spinner size="lg" />
            </Box>
        );
    }

    if (error) {
        return <ErrorState message={translate('game.orders_error_loading')} />;
    }

    if (!orders || orders.length === 0) {
        return (
            <Box p={4}>
                <Text color="gray.500">{translate('game.orders_no_orders_found')}</Text>
            </Box>
        );
    }

    return (
        <TableContainer>
                <Table variant="simple">
                    <Thead>
                        <Tr>
                            <Th>{translate('game.orders_table_user')}</Th>
                            <Th>{translate('game.orders_table_items')}</Th>
                            <Th>{translate('game.orders_table_created')}</Th>
                            <Th>{translate('game.orders_table_status')}</Th>
                            <Th>{translate('game.orders_table_amount')}</Th>
                        </Tr>
                    </Thead>
                    <Tbody>
                        {orders.map((order) => (
                            <OrderRow key={order.id} order={order} />
                        ))}
                    </Tbody>
                </Table>
        </TableContainer>
    );
}

interface OrderRowProps {
    order: OrderDto;
}

function OrderRow({ order }: OrderRowProps) {
    const textColor = useColorModeValue('secondaryGray.900', 'white');

    const getStatusBadge = (status: OrderStatus) => {
        const colorScheme = {
            [OrderStatus.PAID]: 'green',
            [OrderStatus.PENDING]: 'yellow',
            [OrderStatus.CANCELLED]: 'red',
        }[status];

        const getStatusTranslation = (status: OrderStatus) => {
            switch (status) {
                case OrderStatus.PAID:
                    return translate('game.orders_status_paid');
                case OrderStatus.PENDING:
                    return translate('game.orders_status_pending');
                case OrderStatus.CANCELLED:
                    return translate('game.orders_status_cancelled');
                default:
                    return status;
            }
        };

        return (
            <Badge colorScheme={colorScheme} variant="subtle">
                {getStatusTranslation(status)}
            </Badge>
        );
    };

    const formatAmount = (amount: number, currency: string) => {
        return `${amount.toFixed(2)} ${currency.toUpperCase()}`;
    };

    return (
        <Tr>
            <Td color={textColor}>
                <HStack spacing={3}>
                    <Avatar
                        size="sm"
                        name={`${order.userFirstName || ''} ${order.userLastName || ''}`}
                        src={undefined} // No avatar URL available in order data
                    />
                    <VStack align="start" spacing={0}>
                        <Text fontWeight="medium">
                            {order.userFirstName && order.userLastName
                                ? `${order.userFirstName} ${order.userLastName}`
                                : order.userEmail || `User ${order.userId.substring(0, 8)}...`
                            }
                        </Text>
                        <Text fontSize="sm" color="gray.500">
                            {order.userEmail || ''}
                        </Text>
                    </VStack>
                </HStack>
            </Td>
            <Td color={textColor}>
                <VStack align="start" spacing={1}>
                    {order.items.map((item, index) => (
                        <Text key={index} fontSize="xs">
                            {item.productName} x{item.quantity}
                        </Text>
                    ))}
                </VStack>
            </Td>
            <Td color={textColor}>
                <Text fontSize="sm">
                    {formatOrderDate(String(order.createdAt))}
                </Text>
            </Td>
            <Td>
                {getStatusBadge(order.status)}
            </Td>
            <Td color={textColor}>
                <Text fontSize="sm">
                    {formatAmount(order.totalAmount, order.currency)}
                </Text>
            </Td>
        </Tr>
    );
}
